import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle, AlertTriangle,
  Play, ArrowLeft, Info, BookOpen, Target,
  Award, Globe, Wifi,
  ChevronRight, AlertCircle, Zap,
  RefreshCw, Settings, Trophy, GraduationCap
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import { getExamQuestionCount, getExamTotalPointsLabel } from '@/utils/examQuestionCount';
import TierThresholds from '@/components/certifications/TierThresholds';

interface CertificationExamDetails {
  _id: string;
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  questions: any[];
  total_points: number;
  passing_score: number;
  time_limit_minutes?: number;
  max_attempts?: number;
  tier_config?: {
    enabled: boolean;
    gold_threshold: number;
    silver_threshold: number;
    bronze_threshold: number;
  };
  shuffle_questions: boolean;
  shuffle_options: boolean;
  is_published: boolean;
  generated_by: string;
  createdAt: Date;
  updatedAt: Date;
}

export default function LearnerStartExam() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [certificationData, setCertificationData] = useState<CertificationExamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<'overview' | 'instructions' | 'system-check' | 'ready'>('overview');
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [systemChecks, setSystemChecks] = useState({
    browser: false,
    internet: false,
    camera: false,
    microphone: false,
    screenShare: false
  });
  const [isCheckingSystem, setIsCheckingSystem] = useState(false);
  const [showInProgressDialog, setShowInProgressDialog] = useState(false);
  const [isStartingExam, setIsStartingExam] = useState(false);
  const startExamLock = useRef(false); // Blocks double clicks before the disabled state renders
  const [inProgressAttemptId, setInProgressAttemptId] = useState<string | null>(null);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [inProgressViolationCount, setInProgressViolationCount] = useState<number>(0);

  // Fetch certification details from API
  const loadCertificationDetails = useCallback(async () => {
    if (!examId) return;

    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_DETAILS(examId));

      if (response.data.success) {
        setCertificationData(response.data.data);
        console.log('📋 Certification Data:', response.data.data);
      }
    } catch (error) {
      console.error('Error loading certification:', error);
      toast.error('Failed to load certification details');
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    loadCertificationDetails();
  }, [loadCertificationDetails]);

  // Reset checkbox when entering instructions page
  useEffect(() => {
    if (currentStep === 'instructions') {
      setAgreedToRules(false);
    }
  }, [currentStep]);
  
  // Reset system checks when returning to system-check step
  useEffect(() => {
    if (currentStep === 'system-check') {
      // Reset all system checks to force re-checking
      setSystemChecks({
        browser: false,
        internet: false,
        camera: false,
        microphone: false,
        screenShare: false
      });
    }
  }, [currentStep]);

  // Check for in-progress attempt from navigation state (don't show dialog yet)
  useEffect(() => {
    const navigationState = location.state as { inProgressAttemptId?: string; hasInProgressAttempt?: boolean } | null;

    if (navigationState?.hasInProgressAttempt && navigationState?.inProgressAttemptId) {
      console.log('🔄 In-progress attempt detected:', navigationState.inProgressAttemptId);
      setInProgressAttemptId(navigationState.inProgressAttemptId);

      // Clear the navigation state so the dialog doesn't re-appear on refresh/re-navigation
      window.history.replaceState({}, '', window.location.pathname);

      // Pre-fetch violation count to check if Continue should be allowed
      const fetchViolationCount = async () => {
        try {
          const response = await apiClient.get(
            API_ENDPOINTS.CERTIFICATIONS.RETRY_EXAM(navigationState.inProgressAttemptId!, examId!)
          );
          if (response.data.success && response.data.data) {
            const violationCount = response.data.data.violation_count || 0;
            setInProgressViolationCount(violationCount);
            console.log('📊 Pre-fetched violation count:', violationCount);
          }
        } catch (error) {
          console.error('Failed to pre-fetch violation count:', error);
        }
      };

      fetchViolationCount();
      // Skip directly to "ready" step since they already have an in-progress attempt
      setCurrentStep('ready');
      setShowInProgressDialog(true);
    }
  }, [location.state, examId]);

  const handleSystemCheck = async () => {
    setIsCheckingSystem(true);

    try {
      // 1. Check Internet Connection via backend /ping endpoint
      const checkInternet = async () => {
        try {
          if (!navigator.onLine) {
            setSystemChecks(prev => ({ ...prev, internet: false }));
            return false;
          }
          const response = await apiClient.get('/ping');
          const isSuccess = response.data?.status === 'success';
          setSystemChecks(prev => ({ ...prev, internet: isSuccess }));
          return isSuccess;
        } catch (error) {
          console.error('Internet check failed:', error);
          setSystemChecks(prev => ({ ...prev, internet: false }));
          return false;
        }
      };

      // 2. Check Browser Compatibility
      const checkBrowser = () => {
        try {
          const isCompatible = !!(
            navigator.mediaDevices &&
            typeof navigator.mediaDevices.getUserMedia === 'function' &&
            typeof navigator.mediaDevices.getDisplayMedia === 'function'
          );
          setSystemChecks(prev => ({ ...prev, browser: isCompatible }));
          return isCompatible;
        } catch (error) {
          console.error('Browser check failed:', error);
          setSystemChecks(prev => ({ ...prev, browser: false }));
          return false;
        }
      };

      // 3. Check Camera Access
      // const checkCamera = async () => {
      //   try {
      //     const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      //     // Stop the stream immediately after checking
      //     stream.getTracks().forEach(track => track.stop());
      //     setSystemChecks(prev => ({ ...prev, camera: true }));
      //     toast.success('Camera access granted');
      //     return true;
      //   } catch (error: any) {
      //     console.error('Camera check failed:', error);
      //     setSystemChecks(prev => ({ ...prev, camera: false }));
      //     if (error.name === 'NotAllowedError') {
      //       toast.error('Camera access denied. Please allow camera access.');
      //     } else if (error.name === 'NotFoundError') {
      //       toast.error('No camera found on your device.');
      //     } else {
      //       toast.error('Camera check failed: ' + error.message);
      //     }
      //     return false;
      //   }
      // };

      // 4. Check Microphone Access
      // const checkMicrophone = async () => {
      //   try {
      //     const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      //     // Stop the stream immediately after checking
      //     stream.getTracks().forEach(track => track.stop());
      //     setSystemChecks(prev => ({ ...prev, microphone: true }));
      //     toast.success('Microphone access granted');
      //     return true;
      //   } catch (error: any) {
      //     console.error('Microphone check failed:', error);
      //     setSystemChecks(prev => ({ ...prev, microphone: false }));
      //     if (error.name === 'NotAllowedError') {
      //       toast.error('Microphone access denied. Please allow microphone access.');
      //     } else if (error.name === 'NotFoundError') {
      //       toast.error('No microphone found on your device.');
      //     } else {
      //       toast.error('Microphone check failed: ' + error.message);
      //     }
      //     return false;
      //   }
      // };

      // 5. Check Screen Sharing
      // const checkScreenShare = async () => {
      //   try {
      //     const stream = await navigator.mediaDevices.getDisplayMedia({
      //       video: true
      //     });
      //     // Stop the stream immediately after checking
      //     stream.getTracks().forEach(track => track.stop());
      //     setSystemChecks(prev => ({ ...prev, screenShare: true }));
      //     toast.success('Screen sharing access granted');
      //     return true;
      //   } catch (error: any) {
      //     console.error('Screen share check failed:', error);
      //     setSystemChecks(prev => ({ ...prev, screenShare: false }));
      //     if (error.name === 'NotAllowedError') {
      //       toast.error('Screen sharing access denied. Please allow screen sharing.');
      //     } else {
      //       toast.error('Screen sharing check failed: ' + error.message);
      //     }
      //     return false;
      //   }
      // };

      // Run checks sequentially
      await checkInternet();
      checkBrowser();
      // await checkCamera();
      // await checkMicrophone();
      // await checkScreenShare();

    } catch (error) {
      console.error('System check error:', error);
      toast.error('System check failed. Please try again.');
    } finally {
      setIsCheckingSystem(false);
    }
  };

  // Handle abandoning an in-progress attempt
  const handleAbandonAttempt = async () => {
    if (!inProgressAttemptId) {
      toast.error('No attempt ID found');
      return;
    }

    try {
      setIsAbandoning(true);
      console.log('🚫 Abandoning attempt:', inProgressAttemptId);

      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATIONS.ABANDON_ATTEMPT(inProgressAttemptId),
        {} // Empty payload - attempt_id is in URL params only
      );

      if (response.data.success || response.status === 200) {
        console.log('✅ Attempt abandoned successfully');
        toast.success('Previous attempt abandoned successfully');

        // Close dialog and reset state
        setShowInProgressDialog(false);
        setInProgressAttemptId(null);

        // User can now start a fresh exam
        // No navigation needed - they're already on the start page
      } else {
        throw new Error(response.data.message || 'Failed to abandon attempt');
      }
    } catch (error: any) {
      console.error('❌ Error abandoning attempt:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to abandon attempt';
      toast.error(errorMessage);
    } finally {
      setIsAbandoning(false);
    }
  };

  // Handle continuing an in-progress attempt
  const handleContinueAttempt = async () => {
    if (!inProgressAttemptId) {
      toast.error('No attempt ID found');
      return;
    }

    if (!examId) {
      toast.error('Exam ID not found');
      return;
    }

    try {
      console.log('▶️ Continuing attempt:', inProgressAttemptId);
      console.log('   - Exam ID:', examId);

      // Show loading toast
      const loadingToast = toast.loading('Loading your exam progress...');

      // Fetch the in-progress exam attempt details
      const response = await apiClient.get(
        API_ENDPOINTS.CERTIFICATIONS.RETRY_EXAM(inProgressAttemptId, examId)
      );

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (response.data.success && response.data.data) {
        const attemptDetails = response.data.data;
        console.log('✅ In-progress exam details retrieved:', attemptDetails);

        // Store violation count for button state
        const violationCount = attemptDetails.violation_count || 0;
        setInProgressViolationCount(violationCount);

        // Check if violations are too high
        if (violationCount >= 3) {
          console.warn('⚠️ Violation count too high:', violationCount);
          toast.error('Cannot continue - too many violations recorded. Please start a new attempt.');
          return;
        }

        toast.success('Continuing your exam...');

        // Navigate to the exam page with the full attempt details
        navigate(`/learn/exams/do-exam/${examId}`, {
          state: {
            attemptDetails: attemptDetails,
            isContinuing: true // Flag to indicate this is a continuation
          }
        });
      } else {
        throw new Error(response.data.message || 'Failed to load exam progress');
      }
    } catch (error: any) {
      console.error('❌ Error loading exam progress:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load exam progress. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleStartExam = async () => {
    if (startExamLock.current) return;
    startExamLock.current = true;
    setIsStartingExam(true);
    let navigated = false;

    try {
      // Step 1: Call the start exam API. No loading toast yet: the button shows "Starting..."
      // and a rejection (e.g. deadline passed) should show only its error toast.
      const startResponse = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.START_EXAM(examId!));

      if (!startResponse.data.success) {
        // Handle unsuccessful response (success: false in response body)
        const errorMsg = startResponse.data.error?.error_description ||
                        startResponse.data.error?.message ||
                        startResponse.data.message ||
                        'Failed to start exam';
        toast.error(errorMsg);
        return;
      }

      // Step 2: Get the latest attempt details
      const loadingToast = toast.loading('Loading exam details...', { id: 'start-exam-loading' });

      const attemptResponse = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.LATEST_ATTEMPTS(examId!));

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (attemptResponse.data.success) {
        const attemptDetails = attemptResponse.data.data;
        console.log('Exam attempt details:', attemptDetails);

        toast.success('Exam started successfully!');

        // Navigate to the exam page with attempt details
        navigated = true;
        navigate(`/learn/exams/do-exam/${examId}`, {
          state: { attemptDetails }
        });
      } else {
        toast.error('Failed to load exam details');
      }

    } catch (error: any) {
      console.error('Error starting exam:', error);
      toast.dismiss('start-exam-loading');

      // Extract error message from various possible locations in error response
      const errorData = error?.response?.data;
      const errorMessage = errorData?.error?.error_description ||
                          errorData?.error?.message ||
                          errorData?.message ||
                          error?.message ||
                          'Failed to start exam. Please try again.';

      toast.error(errorMessage);
    } finally {
      // Unlock only when staying on this page, so the learner can retry after an error
      if (!navigated) {
        startExamLock.current = false;
        setIsStartingExam(false);
      }
    }
  };

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'overview': return <Info className="w-5 h-5" />;
      case 'instructions': return <BookOpen className="w-5 h-5" />;
      case 'system-check': return <Settings className="w-5 h-5" />;
      case 'ready': return <Play className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  // Only check internet and browser (camera, microphone, screenShare are commented out)
  const allSystemChecksPass = systemChecks.internet && systemChecks.browser;

  // Exam instructions (generic, could be fetched from API in the future)
  const instructions = [
    'Read each question carefully before selecting your answer.',
    'You can navigate between questions using the navigation panel.',
    'Click "Save Response" after answering each question. Answers are not saved automatically.',
    'Only saved answers are counted when you submit, so save every question before submitting.',
    'Submit your exam before the time limit expires.',
    'You cannot pause the exam once started.',
    'Ensure stable internet connection throughout the exam.'
  ];

  const rules = [
    `This exam is timed and must be completed within ${certificationData?.time_limit_minutes || 60} ${(certificationData?.time_limit_minutes || 60) === 1 ? 'minute' : 'minutes'}.`,
    'Answer each question to the best of your ability.',
    'Any form of cheating or unauthorized assistance will result in disqualification.',
    'Keep your workspace clean and minimize distractions.',
    'Only allowed resources may be used during the exam.',
    'Do not close or refresh your browser during the exam.'
  ];

  const systemRequirements = [
    'Stable internet connection (minimum 1 Mbps)',
    'Modern web browser (Chrome, Firefox, Safari, Edge)',
    'Quiet environment with good lighting',
    'Fully charged device or power adapter',
    'Updated browser with JavaScript enabled'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading exam details...</p>
        </div>
      </div>
    );
  }

  if (!certificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Certification exam not found</p>
          <button
            onClick={() => navigate('/learn/certifications')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const minutes_per_question = Math.max(1, Math.floor((certificationData.time_limit_minutes || 60) / (getExamQuestionCount(certificationData) || 1)));

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with Back Button and Title */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 md:p-6 text-white">
        <button
          onClick={() => navigate('/learn/certifications')}
          className="flex items-center gap-2 text-sm md:text-base text-white/90 hover:text-white mb-3 md:mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Certifications
        </button>
        {/* Mobile: icon + "Certification Exam" label on their own row, title
            full-width below it, so a long title isn't indented beside the icon.
            md and up: icon on the left, pinned to the top of the title (it
            never shrinks — the old items-center layout squeezed it into a thin
            pill floating mid-way down a multi-line title). */}
        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <div className="flex items-center gap-2.5 md:block flex-shrink-0">
            <div className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-12 md:h-12 bg-white/20 rounded-xl ring-1 ring-white/30">
              <Trophy className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <p className="md:hidden text-[11px] font-semibold uppercase tracking-wider text-purple-200">
              Certification Exam
            </p>
          </div>
          <div className="min-w-0 flex-1">
            <p className="hidden md:block text-xs font-semibold uppercase tracking-wider text-purple-200 mb-0.5">
              Certification Exam
            </p>
            <h1 className="text-lg md:text-2xl lg:text-3xl font-bold leading-snug break-words">
              {certificationData?.title || 'Certification Exam'}
            </h1>
            <p className="text-purple-100 text-sm md:text-base mt-1.5">
              Review the instructions and start your certification exam
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl border border-gray-200">
        {/* Progress Steps */}
        <div className="p-4 md:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {['overview', 'instructions', 'system-check', 'ready'].map((step, index) => (
              <div key={step} className={`flex items-center ${index < 3 ? 'flex-1' : ''}`}>
                <div
                  className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-full border-2 ${
                    currentStep === step
                      ? 'border-purple-600 bg-purple-600 text-white'
                      : index < ['overview', 'instructions', 'system-check', 'ready'].indexOf(currentStep)
                      ? 'border-green-600 bg-green-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600'
                  }`}
                >
                  {getStepIcon(step)}
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 mx-1 sm:mx-2 h-0.5 ${
                      index < ['overview', 'instructions', 'system-check', 'ready'].indexOf(currentStep)
                        ? 'bg-green-600'
                        : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 max-w-3xl mx-auto">
            <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700">Overview</span>
            <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700">Instructions</span>
            <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700">System Check</span>
            <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700">Ready</span>
          </div>
        </div>

        {/* Exam Overview */}
        {currentStep === 'overview' && (
          <div className="p-4 md:p-6 lg:p-8">
            <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Exam Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{certificationData.time_limit_minutes || 60} {(certificationData.time_limit_minutes || 60) === 1 ? 'minute' : 'minutes'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Questions:</span>
                      <span className="font-medium">{getExamQuestionCount(certificationData)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Total Points:</span>
                      <span className="font-medium">{getExamTotalPointsLabel(certificationData)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Passing Score:</span>
                      <span className="font-medium">{certificationData.passing_score}%</span>
                    </div>
                    {certificationData.max_attempts && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Max Attempts:</span>
                        <span className="font-medium">{certificationData.max_attempts}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    Exam Configuration
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">Questions Shuffled</span>
                      <span className={`flex items-center gap-1 text-sm font-medium ${certificationData.shuffle_questions ? 'text-green-600' : 'text-gray-500'}`}>
                        {certificationData.shuffle_questions ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        {certificationData.shuffle_questions ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">Options Shuffled</span>
                      <span className={`flex items-center gap-1 text-sm font-medium ${certificationData.shuffle_options ? 'text-green-600' : 'text-gray-500'}`}>
                        {certificationData.shuffle_options ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        {certificationData.shuffle_options ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">Published Status</span>
                      <span className={`flex items-center gap-1 text-sm font-medium ${certificationData.is_published ? 'text-green-600' : 'text-amber-600'}`}>
                        {certificationData.is_published ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        {certificationData.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div> */}
            </div>

            <TierThresholds
              tier_config={certificationData.tier_config}
              passing_score={certificationData.passing_score}
              variant="detailed"
              className="mb-8"
            />

            <div className="p-4 sm:p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                {/* Icon + heading on one row, text full-width below (no indent) */}
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <h3 className="font-semibold text-purple-900">Upon Completion</h3>
                </div>
                <p className="text-purple-800 text-sm leading-relaxed">
                  Successfully passing this certification exam will earn you a professional certificate
                  that validates your skills and knowledge. This can boost your career prospects and
                  demonstrate your expertise to employers and peers.
                </p>
              </div>

            <div className="mt-6 sm:mt-8 flex justify-end">
              <button
                onClick={() => setCurrentStep('instructions')}
                className="w-full sm:w-auto justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Instructions */}
        {currentStep === 'instructions' && (
          <div className="p-4 md:p-6 lg:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-purple-600" />
              Exam Instructions & Rules
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Instructions</h3>
                  <ul className="space-y-3">
                    {instructions.map((instruction, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-gray-700 text-sm">{instruction}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Rules & Regulations</h3>
                  <ul className="space-y-3">
                    {rules.map((rule, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{rule}</span>
                      </li>
                    ))}
                  </ul>
              </div>
            </div>

            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1.5">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <span className="font-medium text-amber-900">Important Notice</span>
                </div>
                <p className="text-amber-800 text-sm leading-relaxed">
                  By proceeding with this exam, you acknowledge that you have read and understood all instructions and rules.
                  Any violation may result in automatic disqualification.
                </p>
            </div>

            <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <h3 className="font-semibold text-blue-900">Tips for Success</h3>
                </div>
                <div>
                  <div>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span>Manage your time - aim to spend about {minutes_per_question} {minutes_per_question === 1 ? 'minute' : 'minutes'} per question</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span>Read each question carefully before answering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span>Review your answers before final submission</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">•</span>
                        <span>Stay calm and focused throughout the exam</span>
                      </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToRules}
                    onChange={(e) => setAgreedToRules(e.target.checked)}
                    className="mt-1 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-gray-700 text-sm">
                    I have read and agree to all the instructions and rules mentioned above.
                  </span>
              </label>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                  <button
                    onClick={() => setCurrentStep('overview')}
                    className="w-full sm:w-auto justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep('system-check')}
                    disabled={!agreedToRules}
                    className={`w-full sm:w-auto justify-center whitespace-nowrap px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                      agreedToRules
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    System Check
                    <ChevronRight className="w-4 h-4" />
                  </button>
              </div>
            </div>
          </div>
        )}

        {/* System Check */}
        {currentStep === 'system-check' && (
          <div className="p-4 md:p-6 lg:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Settings className="w-6 h-6 text-purple-600" />
              System Requirements Check
            </h2>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1.5">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-900 font-medium">Permission Request</p>
                </div>
                <div>
                  <div>
                    <p className="text-sm text-blue-800">
                      When you click "Run System Check", your browser will request permission to access your camera, microphone, and screen.
                      Please allow these permissions for the exam to proceed.
                    </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">System Requirements</h3>
                  <ul className="space-y-3">
                    {systemRequirements.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Technical Checks</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'internet', label: 'Internet Connection', icon: <Wifi className="w-5 h-5" /> },
                      { key: 'browser', label: 'Browser Compatibility', icon: <Globe className="w-5 h-5" /> },
                      // { key: 'camera', label: 'Camera Access', icon: <Camera className="w-5 h-5" /> },
                      // { key: 'microphone', label: 'Microphone Access', icon: <Mic className="w-5 h-5" /> },
                      // { key: 'screenShare', label: 'Screen Sharing', icon: <Monitor className="w-5 h-5" /> }
                    ].map(({ key, label, icon }) => (
                      <div key={key} className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                        systemChecks[key as keyof typeof systemChecks]
                          ? 'bg-green-50 border-green-300'
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={systemChecks[key as keyof typeof systemChecks] ? 'text-green-600' : 'text-gray-400'}>
                            {icon}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {systemChecks[key as keyof typeof systemChecks] ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Passed</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-400">
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-sm">Pending</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="text-center mb-8">
                <button
                  onClick={handleSystemCheck}
                  disabled={isCheckingSystem}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 mx-auto ${
                    isCheckingSystem
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isCheckingSystem ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Running Checks...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Run System Check
                    </>
                )}
              </button>
            </div>

            {allSystemChecksPass && (
                <div className="p-4 sm:p-6 bg-green-50 border-2 border-green-300 rounded-lg">
                  <div className="flex items-center gap-2 mb-1.5">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <h3 className="font-semibold text-green-900">All System Checks Passed!</h3>
                  </div>
                  <div>
                    <div>
                      <p className="text-green-800 text-sm">
                        Your system has passed all checks. You're ready to begin your certification exam.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                <button
                  onClick={() => setCurrentStep('instructions')}
                  className="w-full sm:w-auto justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep('ready')}
                  disabled={!allSystemChecksPass}
                  className={`w-full sm:w-auto justify-center whitespace-nowrap px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                    allSystemChecksPass
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Ready to Start
                  <ChevronRight className="w-4 h-4" />
                </button>
            </div>
          </div>
        )}

        {/* Ready to Start */}
        {currentStep === 'ready' && (
          <div className="p-4 md:p-6 lg:p-8 text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
              inProgressAttemptId ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {inProgressAttemptId ? (
                <RefreshCw className="w-10 h-10 text-blue-600" />
              ) : (
                <CheckCircle className="w-10 h-10 text-green-600" />
              )}
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              {inProgressAttemptId ? 'Exam In Progress' : 'Ready to Start Exam'}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              {inProgressAttemptId ? (
                <>
                  You have an exam attempt in progress. You can continue where you left off
                  or abandon your current attempt to start fresh.
                </>
              ) : (
                <>
                  You have completed all the pre-exam requirements. Once you start the exam,
                  the timer will begin and you cannot pause or leave the exam interface.
                </>
              )}
            </p>

            {/* Show warning for in-progress attempts */}
            {inProgressAttemptId && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 max-w-2xl mx-auto text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <h3 className="font-semibold text-blue-900">In-Progress Attempt Detected</h3>
                </div>
                <div>
                  <div>
                    <p className="text-sm text-blue-800">
                      When you click "Continue Exam", you'll be asked whether you want to resume your
                      existing attempt or abandon it and start fresh. Note that abandoning will count
                      as one of your attempts.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* text-left: this step centres its text, which centred the tile
                labels while the values stayed left. Layout: certification name
                as a full-width card, then the three numbers as equal tiles in
                one row (the old 2-col grid left "Passing Score" orphaned). */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 text-left">
                <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4">Final Summary</h3>
                <div className="bg-white rounded-lg p-3 sm:p-4 mb-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Trophy className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Certification</span>
                  </div>
                  <p className="font-semibold text-gray-900 leading-snug break-words">{certificationData.title}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {[
                    {
                      icon: Clock,
                      label: 'Time Limit',
                      value: `${certificationData.time_limit_minutes || 60} ${(certificationData.time_limit_minutes || 60) === 1 ? 'min' : 'mins'}`,
                    },
                    { icon: FileText, label: 'Questions', value: String(getExamQuestionCount(certificationData)) },
                    { icon: Target, label: 'Pass Score', value: `${certificationData.passing_score}%` },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="bg-white rounded-lg p-2.5 sm:p-4 text-center">
                      <Icon className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                      <p className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{value}</p>
                      <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
            </div>

            <div className="bg-amber-50 rounded-lg p-4 sm:p-6 border-2 border-amber-200 mb-6 sm:mb-8">
                <div className="flex items-center gap-2 mb-2 text-left">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <h3 className="font-semibold text-amber-900">Important Reminders</h3>
                </div>
                <div className="text-left">
                  <div>
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-amber-800 marker:text-amber-500">
                      <li>Once you start, the timer will begin immediately</li>
                      <li>You cannot pause the exam once started</li>
                      <li>Make sure you have a stable internet connection</li>
                      <li>Do not close or refresh your browser during the exam</li>
                      <li>You must submit your exam before the time runs out</li>
                      <li>Turn on Do Not Disturb (DND) mode on your device to avoid notification interruptions</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Mobile: primary action full-width on top, Back below it.
                flex-col-reverse keeps Back first in the DOM / tab order. */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3 sm:gap-4">
                <button
                  onClick={() => setCurrentStep('system-check')}
                  className="w-full sm:w-auto justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={() => {
                    // If there's an in-progress attempt, show the dialog first
                    if (inProgressAttemptId) {
                      setShowInProgressDialog(true);
                    } else {
                      // No in-progress attempt, start new exam
                      handleStartExam();
                    }
                  }}
                  disabled={isStartingExam}
                  className={`w-full sm:w-auto justify-center whitespace-nowrap px-8 py-3.5 sm:py-3 rounded-lg font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                    inProgressAttemptId
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                  }`}
                >
                  <Play className="w-5 h-5" />
                  {isStartingExam ? 'Starting...' : inProgressAttemptId ? 'Continue Exam' : 'Start Exam Now'}
                </button>
            </div>
          </div>
        )}
      </div>

      {/* In-Progress Attempt Dialog */}
      {showInProgressDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">Exam In Progress</h3>
              <p className="text-gray-600 mb-6">
                You have an exam attempt in progress. Would you like to continue where you left off or abandon the current attempt and start fresh?
              </p>

              {/* Violation Warning */}
              {inProgressViolationCount >= 3 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-red-900">
                    <strong>⚠️ Cannot Continue:</strong> You have {inProgressViolationCount} violations recorded. Please abandon this attempt and start fresh.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> If you abandon your current attempt, all progress will be lost and it will count as one of your attempts.
                  </p>
                  {inProgressViolationCount > 0 && (
                    <p className="text-sm text-orange-700 mt-2">
                      <strong>Violations:</strong> {inProgressViolationCount}/3 recorded
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleContinueAttempt}
                  disabled={inProgressViolationCount >= 3}
                  className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    inProgressViolationCount >= 3
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Play className="w-5 h-5" />
                  Continue Exam
                </button>
                <button
                  onClick={handleAbandonAttempt}
                  disabled={isAbandoning}
                  className={`w-full px-6 py-3 border-2 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    isAbandoning
                      ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'border-red-300 bg-white text-red-600 hover:bg-red-50'
                  }`}
                >
                  {isAbandoning ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Abandoning...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5" />
                      Abandon & Start Fresh
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
